import React from 'react';
import { css } from '@emotion/react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import OptionFilter from '../modals/OptionFilter';
import { summarizeAssignments } from '../utils';
import { EmptyState, HeaderCell, Skipped, TableCard, TableFooter, TableScroll } from './blocks';
import { SHIPPER_COLUMNS } from './constants';
import ExpandedShipper from './ExpandedShipper';
import { useShipperCarrierSetups } from './hooks';
import ShipperRow from './ShipperRow';
import { pluralize } from './utils';

const ShippersTable = ({
    clients,
    assignmentsByShipper,
    assignmentsUnavailable,
    assignmentsError,
    statusFilters,
    setFilter,
    onOpen,
    onDetails,
    onSignIn,
    onRoutingChange,
    isRoutingUpdating,
}) => {
    const {
        expandedIds,
        toggleExpanded,
        carrierSetupsByShipper,
        marketplaceShippers,
        defaultPricingByCarrier,
        defaultIntegrationsByCarrier,
        loading: setupsLoading,
        error: setupsError,
    } = useShipperCarrierSetups();

    return (
        <TableCard>
            <TableScroll>
                <Table
                    stickyHeader
                    css={css`
                        table-layout: fixed;
                        width: 100%;
                    `}
                >
                    <TableHead>
                        <TableRow>
                            {SHIPPER_COLUMNS.map(({ key, label, width, align, filter }) => (
                                <HeaderCell
                                    key={key}
                                    align={align}
                                    css={
                                        width
                                            ? css`
                                                  width: ${width}px;
                                              `
                                            : undefined
                                    }
                                >
                                    <span
                                        css={css`
                                            align-items: center;
                                            display: inline-flex;
                                            gap: 2px;
                                        `}
                                    >
                                        {label}
                                        {filter && (
                                            <OptionFilter
                                                value={statusFilters[filter.key] || null}
                                                onChange={(value) => setFilter(filter.key, value)}
                                                options={filter.options}
                                            />
                                        )}
                                    </span>
                                </HeaderCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clients.map((client) => {
                            const isExpanded = expandedIds.has(client.client_id);
                            const marketplaceShipper = marketplaceShippers.find(
                                (shipper) => shipper.client_id === client.client_id
                            );
                            // The page's Tailwind is scoped to descendants of `.onward-design`.
                            const expandedRow = isExpanded ? (
                                <TableRow>
                                    <TableCell colSpan={SHIPPER_COLUMNS.length} padding="none">
                                        <div className="onward-design">
                                            <ExpandedShipper
                                                carrierSetups={carrierSetupsByShipper[client.client_id]}
                                                marketplaceShipper={marketplaceShipper}
                                                defaultPricingByCarrier={defaultPricingByCarrier}
                                                defaultIntegrationsByCarrier={defaultIntegrationsByCarrier}
                                                loading={setupsLoading || assignmentsUnavailable}
                                                error={setupsError || assignmentsError}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : null;

                            return (
                                <React.Fragment key={client.client_id}>
                                    <ShipperRow
                                        client={client}
                                        assignment={summarizeAssignments(assignmentsByShipper?.[client.client_id])}
                                        assignmentUnavailable={assignmentsUnavailable}
                                        isExpanded={isExpanded}
                                        onToggleExpanded={() => toggleExpanded(client.client_id)}
                                        onOpen={() => onOpen(client)}
                                        onDetails={() => onDetails(client)}
                                        onSignIn={() => onSignIn(client)}
                                        onRoutingChange={(next) => onRoutingChange(client, next)}
                                        routingDisabled={isRoutingUpdating(client.client_id)}
                                    />
                                    {expandedRow}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
                {clients.length === 0 && <EmptyState>No shippers match the current filters.</EmptyState>}
            </TableScroll>
            <TableFooter>
                <span>
                    Showing {clients.length} {pluralize(clients.length, 'shipper')}
                </span>
                <Skipped />
            </TableFooter>
        </TableCard>
    );
};

export default ShippersTable;
